import React from 'react';
import './Banner.css';

/**
 * Reusable Banner component for Page Headers and Inline Alerts.
 *
 * @param {string} title - Main text or heading
 * @param {string} subtitle - Smaller text below title (page-header only)
 * @param {string} description - Long-form text
 * @param {string} type - 'page-header' | 'info' | 'success' | 'warning' | 'error'
 * @param {React.ReactNode} icon - Custom icon (defaults based on type)
 * @param {function} onClose - If provided, shows a close button
 * @param {React.ReactNode} actions - Action buttons/elements for the right side
 * @param {React.ReactNode} children - Additional content (useful for custom alerts)
 * @param {string} className - Additional CSS classes
 */
const Banner = ({
  title,
  subtitle,
  description,
  type = 'page-header',
  icon,
  onClose,
  actions,
  children,
  className = '',
}) => {
  const isPageHeader = type === 'page-header';

  if (isPageHeader) {
    return (
      <div className={`ui-banner banner-page-header ${className}`}>
        <div className="banner-body">
          <div className="banner-content">
            {title && <h1 className="banner-title">{title}</h1>}
            {(subtitle || description) && (
              <div className="banner-text-stack">
                {subtitle && <p className="banner-subtitle">{subtitle}</p>}
                {description && <p className="banner-description">{description}</p>}
              </div>
            )}
            {children}
          </div>
          {actions && <div className="banner-actions">{actions}</div>}
        </div>
        {onClose && (
          <button className="banner-close-btn" onClick={onClose} aria-label="Close banner">
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
    );
  }

  // Alert/Inline Style
  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'success':
        return <i className="fas fa-check-circle"></i>;
      case 'warning':
        return <i className="fas fa-exclamation-triangle"></i>;
      case 'error':
        return <i className="fas fa-exclamation-circle"></i>;
      case 'info':
      default:
        return <i className="fas fa-info-circle"></i>;
    }
  };

  return (
    <div className={`ui-banner banner-alert banner-${type} ${className}`}>
      <div className="banner-icon">{getIcon()}</div>
      <div className="banner-content">
        {title && <strong className="banner-alert-title">{title}</strong>}
        {description && <p className="banner-alert-desc">{description}</p>}
        {children}
      </div>
      {onClose && (
        <button className="banner-close-btn" onClick={onClose} aria-label="Close banner">
          <i className="fas fa-times"></i>
        </button>
      )}
    </div>
  );
};

export default Banner;
